// The session (Better Auth cookie on `api.`) and the customer it acts for. `GET /app/me` answers 401 without a session;
// the active customer is the one chosen earlier on this browser (a convenience, guarded) or the first one, and the API
// client sends it as `x-customer-id` (A10).
import { MeResponse } from '@req/contracts';
import { useQuery } from '@tanstack/react-query';
import { api, setActiveCustomerId } from './api.ts';

const KEY = 'req.customer';

const stored = (): string | null => {
  try {
    return globalThis.localStorage?.getItem(KEY) ?? null;
  } catch {
    return null;
  }
};

export function rememberCustomer(id: string): void {
  try {
    globalThis.localStorage?.setItem(KEY, id);
  } catch {
    /* private mode / blocked storage: the first customer is used */
  }
  setActiveCustomerId(id);
}

export type Session =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'signed_in'; me: MeResponse; customerId: string | null };

export const meQueryKey = ['me'] as const;

/** S-z: where a signed-in login starts — a consultoria at its Carteira, anyone else at Hoje. */
export function homeOf(me: MeResponse | null | undefined): string {
  const live = me?.customers.filter((c) => c.status !== 'deleted') ?? [];
  const chosen = live.find((c) => c.id === stored()) ?? live[0];
  return chosen?.isConsultant ? '/carteira' : '/';
}

export function useSession(): Session {
  const q = useQuery({
    queryKey: meQueryKey,
    queryFn: async () => {
      const r = await api.get('/app/me', MeResponse);
      if (r.ok) return r.data;
      if (r.status === 401) return null;
      throw new Error(r.error);
    },
    staleTime: 5 * 60_000,
  });
  if (q.isPending) return { status: 'loading' };
  if (!q.data) return { status: 'anonymous' };
  const live = q.data.customers.filter((c) => c.status !== 'deleted');
  const chosen = live.find((c) => c.id === stored()) ?? live[0] ?? null;
  setActiveCustomerId(chosen?.id ?? null);
  return { status: 'signed_in', me: q.data, customerId: chosen?.id ?? null };
}
